import type {
  AllocationParametersUpdate,
  AssignmentType,
  CalendarEntry,
  CapacityAllocationMap,
  CapacityCalendarData,
  PoStatus,
  WeekCapacityAllocation,
} from '@/types/capacityCalendar';
import type { ManualAllocationsPayload, ManualAllocationsResult } from '@/types/capacityImport';
import apiClient from './axios';

interface ApiCalendarPo {
  id: string;
  type?: AssignmentType;
  poNumber: string | null;
  quantity: number;
  status: string | null;
  notes: string | null;
  distributionGroupId?: string | null;
  productCategory?: string | null;
}

interface ApiCalendarAllocation {
  id: string;
  weekKey: string;
  weekNumber: number;
  capacity: number | null;
  lineQty: number | null;
  vendorInput: number | null;
  vendorRemained: number | null;
  capacityFactor: number | null;
  planningCapacity: number;
  remainedCapacity: number;
  source: 'import' | 'manual';
  purchaseOrders: ApiCalendarPo[];
}

interface ApiCalendarResponse {
  entries: CalendarEntry[];
  allocations: Record<string, Record<string, ApiCalendarAllocation>>;
  lockedVendorIds: string[];
}

interface ApiDistributionResult {
  capacityTypeId: string;
  allocation: ApiCalendarAllocation;
}

function toWeekAllocation(allocation: ApiCalendarAllocation): WeekCapacityAllocation {
  return {
    id: allocation.id,
    weekKey: allocation.weekKey,
    purchaseOrders: allocation.purchaseOrders.map((po) => ({
      id: po.id,
      type: po.type,
      poNumber: po.poNumber,
      quantity: po.quantity,
      status: (po.status as PoStatus | null) ?? undefined,
      notes: po.notes ?? undefined,
      distributionGroupId: po.distributionGroupId ?? null,
      productCategory: po.productCategory ?? null,
    })),
    remainedCapacity: allocation.remainedCapacity,
    capacity: allocation.capacity,
    lineQty: allocation.lineQty,
    vendorInput: allocation.vendorInput,
    planningCapacity: allocation.planningCapacity,
    capacityFactor: allocation.capacityFactor ?? undefined,
    vendorRemained: allocation.vendorRemained,
    source: allocation.source,
  };
}

export async function fetchCapacityCalendar(): Promise<CapacityCalendarData> {
  const { data } = await apiClient.get<ApiCalendarResponse>('/capacity/calendar', { skipGlobalError: true });
  const allocations: CapacityAllocationMap = {};
  Object.entries(data.allocations).forEach(([entryId, byWeek]) => {
    allocations[entryId] = Object.fromEntries(Object.entries(byWeek).map(([weekKey, allocation]) => [weekKey, toWeekAllocation(allocation)]));
  });
  // Tolerate a backend that predates lockedVendorIds — an absent list means "nothing locked",
  // not a render-time crash in every consumer that calls .includes() on it.
  return { entries: data.entries, allocations, lockedVendorIds: data.lockedVendorIds ?? [] };
}

export async function updateCapacityAllocation(id: string, update: AllocationParametersUpdate): Promise<void> {
  await apiClient.patch(`/capacity/allocations/${id}`, update, { skipGlobalError: true });
}

export async function deleteCapacityAllocation(id: string): Promise<void> {
  await apiClient.delete(`/capacity/allocations/${id}`, { skipGlobalError: true });
}

export async function createManualAllocations(payload: ManualAllocationsPayload): Promise<ManualAllocationsResult> {
  const { data } = await apiClient.post<ManualAllocationsResult>('/capacity/allocations', payload, {
    skipGlobalError: true,
  });
  return data;
}

export interface PoAssignmentInput {
  type?: AssignmentType;
  poNumber?: string;
  quantity: number;
  notes?: string;
  productCategory?: string;
}

export async function createPoAssignments(allocationId: string, assignments: PoAssignmentInput[]): Promise<WeekCapacityAllocation> {
  const { data } = await apiClient.post<ApiCalendarAllocation>(
    `/capacity/allocations/${allocationId}/assignments`,
    { assignments },
    { skipGlobalError: true },
  );
  return toWeekAllocation(data);
}

export async function updatePoAssignment(allocationId: string, assignmentId: string, quantity: number): Promise<WeekCapacityAllocation> {
  const { data } = await apiClient.patch<ApiCalendarAllocation>(
    `/capacity/allocations/${allocationId}/assignments/${assignmentId}`,
    { quantity },
    { skipGlobalError: true },
  );
  return toWeekAllocation(data);
}

export interface DistributionPortion {
  capacityTypeId: string;
  quantity: number;
}

export interface DistributePurchaseOrderPayload {
  vendorId: string;
  weekStart: string;
  // Absent for a manual block: the backend writes the rows as manual-type assignments.
  poNumber?: string;
  portions: DistributionPortion[];
  notes?: string;
  productCategory?: string;
}

export interface DistributionResult {
  capacityTypeId: string;
  allocation: WeekCapacityAllocation;
}

function toDistributionResults(data: ApiDistributionResult[]): DistributionResult[] {
  return data.map((item) => ({
    capacityTypeId: item.capacityTypeId,
    allocation: toWeekAllocation(item.allocation),
  }));
}

export async function distributePurchaseOrder(payload: DistributePurchaseOrderPayload): Promise<DistributionResult[]> {
  const { data } = await apiClient.post<ApiDistributionResult[]>('/capacity/allocations/distribute', payload, {
    skipGlobalError: true,
  });
  return toDistributionResults(data);
}

export async function deletePoAssignment(allocationId: string, assignmentId: string): Promise<DistributionResult[]> {
  const { data } = await apiClient.delete<ApiDistributionResult[]>(`/capacity/allocations/${allocationId}/assignments/${assignmentId}`, {
    skipGlobalError: true,
  });
  return toDistributionResults(data);
}

// Returns every touched allocation, not just this one: the backend cascades the note across
// a distributed block's sibling rows, which live on other capacity types' allocations.
export async function updateAssignmentNotes(allocationId: string, assignmentId: string, notes: string): Promise<DistributionResult[]> {
  const { data } = await apiClient.patch<ApiDistributionResult[]>(
    `/capacity/allocations/${allocationId}/assignments/${assignmentId}/notes`,
    { notes },
    { skipGlobalError: true },
  );
  return toDistributionResults(data);
}

export async function moveAssignment(allocationId: string, assignmentId: string, targetWeekStart: string): Promise<DistributionResult[]> {
  const { data } = await apiClient.patch<ApiDistributionResult[]>(
    `/capacity/allocations/${allocationId}/assignments/${assignmentId}/move`,
    { targetWeekStart },
    { skipGlobalError: true },
  );
  return toDistributionResults(data);
}

export async function moveDistributionGroup(distributionGroupId: string, targetWeekStart: string): Promise<DistributionResult[]> {
  const { data } = await apiClient.patch<ApiDistributionResult[]>(
    `/capacity/distribution-groups/${distributionGroupId}/move`,
    { targetWeekStart },
    { skipGlobalError: true },
  );
  return toDistributionResults(data);
}
